import { useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
// Components
import { ReactComponent as ArrowIcon } from '../../../assets/icons/xeu-white-arrow-right.svg';
// Store
import { setSelectedBelt, setSelectedBeltRef } from '../../../store/belt';
// Services
import api, { API_PATHS } from '../../../service/api';
// Types
import { BaggageArrivalFlight, BaggageTransferFlight } from '../../../types';
// Styles
import tableRowStyles from '../../../styles/table/TableRow.module.css';
import { getBackgroundColorForAEUTEUTableRows } from '../../../service/tableRowBackgroundColorService';
import { setFlightToAnimate } from '../../../store/animation';
import InBlockTableBlock from './InBlockTableBlock';
import FirstBagTableBlock from './FirstBagTableBlock';
import TitleAndOriginTableBlock from './TitleAndOriginTableBlock';
import GreyDividerDot from '../../UI/GreyDividerDot';
import StandardButtonLong from '../../UI/StandardButtonLong';
import { hasCustomsConflictOnTargetBelt } from '../../../service/filterService';

export const AEUTEUReallocationRow = ({
	flight,
	targetBeltAllocations
}: {
	flight: BaggageArrivalFlight | BaggageTransferFlight;
	targetBeltAllocations?: BaggageTransferFlight[];
}) => {
	const dispatch = useAppDispatch();
	const { currentBelt, currentBeltRef } = useAppSelector((state) => state.beltStore);
	const baggageType = useAppSelector((state) => state.pathStore.baggageType);

	const [error, setError] = useState<string>();
	const isMoveBlockedByCustomsOnTargetBelt = hasCustomsConflictOnTargetBelt(
		flight,
		targetBeltAllocations
	);

	const state = {
		flightRef: flight.flightRef,
		baggageType: baggageType,
		fromBeltRef: flight.beltRef,
		toBeltRef: currentBeltRef
	};

	// Handle move to current belt - this also happens in TransferModal.tsx
	const handleMoveToCurrentBelt = async () => {
		setError(undefined);
		try {
			const response = await api.post(API_PATHS.reallocate, state);
			if (response.status === 208) {
			} else if (response.status < 300) {
				dispatch(setFlightToAnimate(flight.flightRef));
				dispatch(setSelectedBelt(currentBelt));
				dispatch(setSelectedBeltRef(currentBeltRef!));
			}
		} catch (err: any) {
			setError(err.message);
		}
	};

	/**
	 * Flights are only allowed to be moved if:
	 * 1. Flight fulfills requirements set by airport (expressed by backend) and first is not set.
	 * 2. Flight is not locked for customs operation
	 */
	const isMoveToNewBeltAllowed =
		baggageType === 'PRIMARY' && !flight.actualFirstBag && !flight.lockedForCustomsOperation;

	return (
		<tr
			className={`${tableRowStyles.tableRow} ${
				tableRowStyles.aeuTeuTableRow
			} table-font ${getBackgroundColorForAEUTEUTableRows(flight)}`}
		>
			<td className="w-[75%] flex items-center" key="tableRow-1">
				<TitleAndOriginTableBlock flight={flight} />
				<GreyDividerDot />
				<InBlockTableBlock flight={flight} />
				{flight.actualFirstBag && !flight.noBax && (
					<>
						<GreyDividerDot />
						<FirstBagTableBlock flight={flight} />
					</>
				)}
			</td>

			<td className={`h-100 ${tableRowStyles.tableRowBlock} w-full-important justify-end`}>
				{error && (
					<div className="red-wrapper flex gap-3 rounded-full mr-3 p-3">
						<p>{error}</p>
					</div>
				)}
				<StandardButtonLong
					disabled={!isMoveToNewBeltAllowed || isMoveBlockedByCustomsOnTargetBelt}
					onClick={handleMoveToCurrentBelt}
				>
					<ArrowIcon width={18} height={18} />
					Move to {currentBelt}
				</StandardButtonLong>
			</td>
		</tr>
	);
};
